import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { ActionControlId, MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import type { RibbonControlId } from 'owa-ribbon-ids';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import { getControlInfo } from 'owa-mail-ribbon-utils';
import * as ControlDef from './slrInsertTabControlDefinitions';
import * as SharedControlDef from '../../../sharedControls/composeSharedControlDefinitions';
import { getAddInlineImagesControl } from 'owa-compose-actioncontrols/lib/components/controls/inlineImages';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { onAddInlineImage } from 'owa-mail-compose-ribbon';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import loc from 'owa-localize';
import { getFindTime } from 'owa-compose-actioncontrols/lib/components/controls/findTime';
import { getAddIns } from 'owa-compose-actioncontrols/lib/components/controls/addins';
import { lazyInsertContentForFindTime } from 'findtime-compose';
import { noSubject } from 'owa-locstrings/lib/strings/nosubject.locstring.json';
import type EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { FluidOwaSource, isFluidEnabledForSource } from 'owa-fluid-validations';
import getFluidHeroMenuDefinition from 'owa-mail-compose-view-shared/lib/utils/getFluidHeroMenuDefinition';
import { getFluidHero } from 'owa-compose-actioncontrols/lib/components/controls/fluidHero';
import { isFeatureEnabled } from 'owa-feature-flags';
import { callEditorApi } from 'owa-editor/lib/utils/callEditorApi';

/**
 * This function supplies a mapping from RibbonControlId to their respective control definition function.
 * For any new control in a tab, be sure to update the mapping below.
 * Note: This function should *not* have any logic regarding showing/hiding.
 *       That should be in shouldShowRibbonControl.ts instead.
 */
export const getSLRInsertTabControlDefinition = computedFn(
    (
        controlId: RibbonControlId,
        props: RibbonControlDefinitionProps
    ): RibbonControlProps | undefined => {
        const viewState: ComposeViewState | undefined =
            props.popoutComposeViewState ?? getComposeViewState();
        if (!viewState) {
            return undefined;
        }
        const editorId: string = viewState.editorId;

        switch (controlId) {
            case ActionControlId.InsertTable:
                return SharedControlDef.getTableControl(viewState);
            case MailRibbonControlId.AttachFile:
                return SharedControlDef.getAttachFileControl(
                    props.attachmentPickers!.addFilesAsAttachments!, // Cannot be undefined, because shouldShowRibbonControl checks for this
                    viewState,
                    MailRibbonTabId.Tab_Insert
                );
            case MailRibbonControlId.AttachSig:
                return SharedControlDef.getAttachSigControl(viewState, MailRibbonTabId.Tab_Insert);
            case MailRibbonControlId.Poll:
                return ControlDef.getPollControl(viewState);
            case MailRibbonControlId.Pictures:
                const controlInfo = getControlInfo(MailRibbonControlId.Pictures);
                return getAddInlineImagesControl(
                    getComposeRibbonId(MailRibbonControlId.Pictures, viewState),
                    onAddInlineImage(props.attachmentPickers!.addImagesInline!.computer.current),
                    undefined /* styles */,
                    controlInfo.text,
                    controlInfo.tooltip,
                    false /* isButtonDisabled */,
                    constructKeytip(
                        [getKeytipString(MailRibbonTabId.Tab_Insert)],
                        getKeytipString(MailRibbonControlId.Pictures)
                    ),
                    controlInfo.iconName
                );
            case MailRibbonControlId.Emojis:
                return SharedControlDef.getEmojisControl(viewState, props.targetWindow);
            case MailRibbonControlId.ShowLinkDialog:
                return SharedControlDef.getLinkControl(viewState, props.targetWindow);

            case ActionControlId.GetAddIns:
                return getAddIns(editorId, undefined);
            case ActionControlId.FindTime:
                const getAllRecipientsFunc = (composeViewState?: ComposeViewState) => () => {
                    return [
                        composeViewState?.toRecipientWell?.recipients?.map(
                            recipient => recipient.persona.EmailAddress
                        ) ?? [],
                        composeViewState?.ccRecipientWell?.recipients?.map(
                            recipient => recipient.persona.EmailAddress
                        ) ?? [],
                        composeViewState?.bccRecipientWell?.recipients?.map(
                            recipient => recipient.persona.EmailAddress
                        ) ?? [],
                    ] as EmailAddressWrapper[][];
                };
                const getSubjectFunc = (composeViewState?: ComposeViewState) => () => {
                    return (composeViewState?.subject || '').trim() || loc(noSubject);
                };
                const insertContentForFindTimeFunc = (editorViewState?: EditorViewState) => (
                    content: string
                ) => {
                    if (editorViewState) {
                        return lazyInsertContentForFindTime.importAndExecute(
                            editorViewState,
                            content
                        );
                    }
                    return null;
                };
                return getFindTime(
                    editorId,
                    'MailComposeView' /* source */,
                    getAllRecipientsFunc(viewState),
                    getSubjectFunc(viewState),
                    insertContentForFindTimeFunc(viewState),
                    undefined
                );
            case ActionControlId.Fluid:
                const isDisabled =
                    viewState.bodyType != 'HTML' ||
                    !isFluidEnabledForSource(FluidOwaSource.MailCompose) ||
                    !isFeatureEnabled('cmp-fluid-createNew');
                const targetWindow = callEditorApi(viewState, 'getDocument')?.defaultView || window;
                return getFluidHero(
                    editorId,
                    getFluidHeroMenuDefinition(viewState, targetWindow),
                    undefined,
                    isDisabled
                );

            default:
                throw new Error(
                    'Unknown control: ' +
                        controlId +
                        ' searched for in getSLRInsertTabControlDefinition'
                );
        }
    }
);
